/**
 * Appointment data service for the Daily Cockpit.
 *
 * Reads from the new multi-source appointments collection at
 *   organizations/{orgId}/clinics/{clinicId}/appointments/{identityHash}
 *
 * Each doc is deduped by identity hash so Revolution and CSV writes for
 * the same patient/day collapse to one row. Latest source wins, wholly.
 *
 * The cockpit consumer is unchanged — `AppointmentPatient` shape is
 * preserved here as a translation layer over the new `Appointment` doc
 * so we can rewire the data source without touching `DailyCockpit.tsx`.
 */

import {
  query, where, onSnapshot, collectionGroup, type Query,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '../config'
import { appointmentsCollection, appointmentConverter } from './firestore'
import type { AppointmentPatient, Appointment, CockpitEligibilityStatus } from '../types'
import { isRealPatient } from '../utils/patientFilters'
import { countPatients } from '../utils/patientGrouping'
import { rollupOf, type EligibilityDisplayStatus } from '../../shared/eligibilityDisplayStatus'

// ── Public types ─────────────────────────────────────────────────────

export type TimeHorizon = 'today' | 'tomorrow' | 'next7days'

export interface CockpitSummary {
  /** Rollup counts — the 3-color glance (🟢 resolved / 🟡 attention / 🔴 broken)
   *  plus the ⚪ neutral group. Placement of each status lives in
   *  shared/eligibilityDisplayStatus.ROLLUP_OF. */
  resolved: number
  attention: number
  broken: number
  neutral: number
  /** Coverage rows — each appointment doc is a patient×coverage×provider
   *  identity, so this counts coverages, not humans. */
  total: number
  /** Distinct humans (conservative grouping by clinic+day+name+DOB — see
   *  utils/patientGrouping). The number the day tiles and header label
   *  "patients"; never lower than reality, reads high when identity fields
   *  drift between sources. */
  patients: number
  /** Per-detailed-status counts, for the hover breakdown on each rollup segment. */
  byStatus: Record<EligibilityDisplayStatus, number>
}

// ── Date helpers ─────────────────────────────────────────────────────

/** Display format used by the cockpit UI (MM/DD/YYYY). The new appointments
 *  collection stores ISO YYYY-MM-DD; this helper bridges the two. */
export function toFirestoreDateStr(date: Date): string {
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${mm}/${dd}/${date.getFullYear()}`
}

/** Parse MM/DD/YYYY string back to a Date (avoids spec-undefined `new Date()` parsing). */
export function parseFirestoreDateStr(str: string): Date {
  const parts = str.split('/')
  if (parts.length !== 3) throw new Error(`Expected MM/DD/YYYY, got: "${str}"`)
  return new Date(+parts[2], +parts[0] - 1, +parts[1])
}

/** YYYY-MM-DD format used by the new appointments.appointmentDate field. */
function toIsoDateStr(date: Date): string {
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/** Same horizon, ISO format (matches appointments.appointmentDate field). */
function getHorizonDatesIso(horizon: TimeHorizon, baseDate: Date): string[] {
  return horizonDateRange(horizon, baseDate).map(toIsoDateStr)
}

function horizonDateRange(horizon: TimeHorizon, baseDate: Date): Date[] {
  switch (horizon) {
    case 'today':
      return [new Date(baseDate)]
    case 'tomorrow': {
      const tomorrow = new Date(baseDate)
      tomorrow.setDate(tomorrow.getDate() + 1)
      return [tomorrow]
    }
    case 'next7days': {
      const dates: Date[] = []
      for (let i = 0; i < 7; i++) {
        const d = new Date(baseDate)
        d.setDate(d.getDate() + i)
        dates.push(d)
      }
      return dates
    }
  }
}

/** Parse an ISO date string (YYYY-MM-DD) into a local-midnight Date. */
export function parseIsoDateStr(str: string): Date {
  return new Date(str + 'T00:00:00')
}

/** Convert YYYY-MM-DD ISO to MM/DD/YYYY display format. */
function isoToFirestoreDate(iso: string): string {
  const parts = iso.split('-')
  if (parts.length !== 3) return iso
  const [y, m, d] = parts
  return `${m}/${d}/${y}`
}

/** Convert MM/DD/YYYY (display) to YYYY-MM-DD (ISO). Used by Dashboard
 *  callers that have a patient.appointmentDate in display format and
 *  need ISO for downstream APIs. */
export function firestoreDateToIso(mmddyyyy: string): string {
  const parts = mmddyyyy.split('/')
  if (parts.length !== 3) return mmddyyyy
  const [mm, dd, yyyy] = parts
  return `${yyyy}-${mm}-${dd}`
}

/** Format a date as "Thu, Mar 5" style. */
export function formatCockpitDate(date: Date): string {
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

// ── Summary computation ──────────────────────────────────────────────

export function computeSummary(patients: AppointmentPatient[]): CockpitSummary {
  const byStatus: Record<EligibilityDisplayStatus, number> = {
    eligible: 0, not_eligible: 0, needs_review: 0, data_issue: 0,
    error: 0, stale: 0, in_progress: 0, not_checked: 0,
  }
  let resolved = 0
  let attention = 0
  let broken = 0
  let neutral = 0

  for (const p of patients) {
    // null (no check yet) reads as not_checked → neutral.
    const status: EligibilityDisplayStatus = p.eligibilityStatus ?? 'not_checked'
    byStatus[status]++
    switch (rollupOf(status)) {
      case 'resolved': resolved++; break
      case 'attention': attention++; break
      case 'broken': broken++; break
      case 'neutral': neutral++; break
    }
  }

  return {
    resolved, attention, broken, neutral,
    total: patients.length,
    patients: countPatients(patients),
    byStatus,
  }
}

// ── Translation: Appointment → AppointmentPatient ────────────────────
// Legacy AppointmentPatient interface kept so DailyCockpit + helpers don't
// have to be touched. New shape is richer (always-set time, denormalized
// orgId, etc.) but the cockpit only consumes the subset below.

/** Pre-#85 legacy values that may exist on un-refreshed docs. Normalized
 *  here so the cockpit's switch statement covers only the current set. */
function normalizeEligibilityStatus(
  raw: CockpitEligibilityStatus | 'review_needed' | 'skipped_retrospective' | null | undefined,
): CockpitEligibilityStatus {
  if (!raw) return 'not_checked'
  if (raw === 'review_needed') return 'needs_review'
  if (raw === 'skipped_retrospective') return 'not_checked'
  return raw
}

function appointmentToCockpitPatient(appt: Appointment): AppointmentPatient {
  return {
    id: appt.id,
    firstName: appt.firstName,
    lastName: appt.lastName,
    dob: appt.dob || null,
    appointmentDate: isoToFirestoreDate(appt.appointmentDate),
    policyNumber: appt.policyNumber,
    payer: appt.payer,
    policyType: appt.policyType,
    priority: appt.priority,
    eligibilityCheckId: appt.eligibilityCheckId,
    eligibilityCheckSetId: appt.eligibilityCheckSetId,
    // Flow key of the row's correction chain. Legacy docs predate the field —
    // their setId is what checks were backfilled with, so it IS the flow key.
    flowId: appt.flowId ?? appt.eligibilityCheckSetId ?? null,
    eligibilityStatus: normalizeEligibilityStatus(appt.eligibilityStatus),
    eligibilityIssue: appt.eligibilityIssue,
    createdAt: appt.createdAt,
    appointmentStartMin: appt.appointmentStartMin,
    appointmentEndMin: appt.appointmentEndMin,
    providerName: appt.doctorName ?? undefined,
    providerId: appt.providerId ?? undefined,
    clinicId: appt.clinicId,
    source: appt.source,
    appointmentTimeIsDefault: appt.appointmentTimeIsDefault,
    csvFileName: appt.csvFileName,
    csvUploadedAt: appt.csvUploadedAt,
  }
}

// ── Firestore subscription ───────────────────────────────────────────

/**
 * Subscribe to appointments for the cockpit's 7-day window.
 *
 * Non-admin: queries the user's clinic subcollection directly.
 * Admin: uses `collectionGroup('appointments')` to span all clinics in the
 * org. `orgId` is denormalized on each appointment doc so the collectionGroup
 * query can scope to one org.
 *
 * Cancelled appointments (re-pulled and missing from the new payload) are
 * filtered out client-side rather than via `where('cancelled', '==', false)`
 * to avoid an extra composite index for the date+cancelled combination.
 *
 * @returns Unsubscribe function — call on component unmount.
 */
export function subscribeToAppointmentPatients(
  orgId: string,
  clinicId: string | null,
  isAdmin: boolean,
  baseDate: Date,
  onData: (patients: AppointmentPatient[]) => void,
  onError: (error: Error) => void,
): () => void {
  const dateWindowIso = getHorizonDatesIso('next7days', baseDate)

  // Admins read the org-wide collectionGroup; non-admins their clinic's
  // subcollection. A null clinic must never reach the clinic branch — a null
  // path segment throws synchronously inside collection() — so refuse it
  // through the error channel the caller already handles.
  let apptQuery: Query<Appointment>
  if (isAdmin) {
    apptQuery = query(
      collectionGroup(db, 'appointments').withConverter(appointmentConverter),
      where('orgId', '==', orgId),
      where('appointmentDate', 'in', dateWindowIso),
    )
  } else {
    if (!clinicId) {
      onError(new Error('Non-admin appointment subscription requires a clinic.'))
      return () => {}
    }
    apptQuery = query(
      appointmentsCollection(orgId, clinicId),
      where('appointmentDate', 'in', dateWindowIso),
    )
  }

  return onSnapshot(apptQuery, (snapshot) => {
    const patients = snapshot.docs
      .map((d) => d.data())
      .filter((appt) => !appt.cancelled)
      .map(appointmentToCockpitPatient)
      .filter(isRealPatient)
    onData(patients)
  }, onError)
}

/** One row of `initiateAppointmentPull`'s answer, per date. */
export interface AppointmentPullResult {
  action: string
  reason?: string
}

/**
 * Triggers a Revolution pull for one clinic and date. `appointmentDate` is
 * MM/DD/YYYY, the format the callable expects.
 */
export async function initiateAppointmentPull(input: {
  orgId: string
  clinicId: string
  appointmentDate: string
}): Promise<{ results?: AppointmentPullResult[] }> {
  const fn = httpsCallable<typeof input, { results?: AppointmentPullResult[] }>(
    functions,
    'initiateAppointmentPull',
  )
  const res = await fn(input)
  return res.data
}
